import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

export interface CustomerCredit {
  id: string;
  customer_id: string;
  credit_amount: number;
  remaining_amount: number;
  source_type: string;
  source_invoice_id: string | null;
  description: string | null;
  status: 'issued' | 'applied' | 'refunded' | 'cancelled';
  credit_note_number: string | null;
  credit_note_issued_at: string | null;
  credit_note_issued_by: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  cancelled_by: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  source_invoice?: {
    invoice_number: string | null;
  } | null;
}

export interface CreditRefund {
  id: string;
  credit_id: string;
  refund_amount: number;
  refund_method: string;
  refund_date: string;
  notes: string | null;
  created_by: string;
  created_at: string;
}

export const useCustomerCredits = (customerId: string | null) => {
  const queryClient = useQueryClient();
  const { isReadOnly } = useImpersonationGuard();

  const { data: credits, isLoading } = useQuery({
    queryKey: ['customerCredits', customerId],
    queryFn: async () => {
      if (!customerId) return [];
      
      const { data, error } = await supabase
        .from('customer_credits')
        .select(`
          *,
          source_invoice:invoices!customer_credits_source_invoice_id_fkey(invoice_number)
        `)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as CustomerCredit[];
    },
    enabled: !!customerId,
  });

  const totalAvailableCredit = credits?.reduce((sum, credit) => {
    if (credit.status === 'issued') {
      return sum + credit.remaining_amount;
    }
    return sum;
  }, 0) || 0;

  const activeCredits = credits?.filter(c => c.status === 'issued' && c.remaining_amount > 0) || [];

  // Apply credit to invoice mutation - Credits REDUCE invoice value, not treated as payment
  const applyCreditMutation = useMutation({
    mutationFn: async ({ creditId, invoiceId, amount }: { creditId: string; invoiceId: string; amount: number }) => {
      if (isReadOnly) throw new Error('Cannot manage customer credits in View Only mode');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current credit
      const { data: credit, error: creditError } = await supabase
        .from('customer_credits')
        .select('remaining_amount')
        .eq('id', creditId)
        .single();

      if (creditError) throw creditError;
      if (!credit) throw new Error('Credit not found');
      if (amount > credit.remaining_amount) throw new Error('Amount exceeds remaining credit');

      // Get invoice to check net amount and determine application status
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('total_amount, credit_applied, status, estimate_id')
        .eq('id', invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      // Calculate net amount (total - existing credits)
      const currentCreditApplied = invoice.credit_applied || 0;
      const netAmount = (invoice.total_amount || 0) - currentCreditApplied;

      // Can't apply more than the net amount
      const amountToApply = Math.min(amount, netAmount);
      if (amountToApply <= 0) throw new Error('Invoice already has credits covering the full amount');

      // Determine application status based on invoice status
      // If invoice is draft, credit is "reserved" until invoice is sent
      const applicationStatus = invoice.status === 'draft' ? 'reserved' : 'finalized';

      // Create credit application with status
      const { error: appError } = await supabase
        .from('credit_applications')
        .insert({
          credit_id: creditId,
          invoice_id: invoiceId,
          amount_applied: amountToApply,
          applied_by: user.id,
          application_status: applicationStatus,
        });

      if (appError) throw appError;

      // Update credit remaining amount and status
      const newRemaining = credit.remaining_amount - amountToApply;
      const newStatus = newRemaining <= 0 ? 'applied' : 'issued';

      const { error: updateError } = await supabase
        .from('customer_credits')
        .update({ 
          remaining_amount: newRemaining,
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', creditId);

      if (updateError) throw updateError;

      // Update invoice credit_applied field (NOT paid_amount - credits reduce value, not payments)
      const newCreditApplied = currentCreditApplied + amountToApply;

      const { error: invoiceUpdateError } = await supabase
        .from('invoices')
        .update({ 
          credit_applied: newCreditApplied,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      if (invoiceUpdateError) throw invoiceUpdateError;

      // Update estimate's total_invoiced to reflect the credit reduction
      // This prevents the credited amount from appearing as "still owed" at project level
      if (invoice.estimate_id) {
        const { data: estimate } = await supabase
          .from('estimates')
          .select('total_invoiced')
          .eq('id', invoice.estimate_id)
          .single();

        if (estimate) {
          await supabase
            .from('estimates')
            .update({ 
              total_invoiced: Math.max(0, (estimate.total_invoiced || 0) - amountToApply),
              updated_at: new Date().toISOString()
            })
            .eq('id', invoice.estimate_id);
        }
      }

      return { creditId, invoiceId, amount: amountToApply };
    },
    onSuccess: () => {
      toast.success('Credit applied successfully');
      queryClient.invalidateQueries({ queryKey: ['customerCredits', customerId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
    },
    onError: (error) => {
      toast.error(`Failed to apply credit: ${error.message}`);
    },
  });

  // Refund credit mutation
  const refundCreditMutation = useMutation({
    mutationFn: async ({ creditId, amount, method, refundDate, notes }: { 
      creditId: string; 
      amount: number; 
      method: string; 
      refundDate: string;
      notes?: string;
    }) => {
      if (isReadOnly) throw new Error('Cannot manage customer credits in View Only mode');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create refund record
      const { error: refundError } = await supabase
        .from('credit_refunds')
        .insert({
          credit_id: creditId,
          refund_amount: amount,
          refund_method: method,
          refund_date: refundDate,
          notes: notes || null,
          created_by: user.id,
        });

      if (refundError) throw refundError;

      // Update credit status
      const { error: updateError } = await supabase
        .from('customer_credits')
        .update({ 
          status: 'refunded',
          remaining_amount: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', creditId);

      if (updateError) throw updateError;

      return { creditId };
    },
    onSuccess: () => {
      toast.success('Credit refunded successfully');
      queryClient.invalidateQueries({ queryKey: ['customerCredits', customerId] });
    },
    onError: (error) => {
      toast.error(`Failed to refund credit: ${error.message}`);
    },
  });

  // Cancel credit mutation
  const cancelCreditMutation = useMutation({
    mutationFn: async ({ creditId, reason }: { creditId: string; reason: string }) => {
      if (isReadOnly) throw new Error('Cannot manage customer credits in View Only mode');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('customer_credits')
        .update({ 
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_reason: reason,
          cancelled_by: user.id,
          remaining_amount: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', creditId);

      if (error) throw error;

      return { creditId };
    },
    onSuccess: () => {
      toast.success('Credit cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['customerCredits', customerId] });
    },
    onError: (error) => {
      toast.error(`Failed to cancel credit: ${error.message}`);
    },
  });

  // Create manual credit mutation
  const createManualCreditMutation = useMutation({
    mutationFn: async ({ amount, reasonType, description, internalDescription }: { 
      amount: number; 
      reasonType: string; 
      description?: string;
      internalDescription?: string;
    }) => {
      if (isReadOnly) throw new Error('Cannot manage customer credits in View Only mode');
      if (!customerId) throw new Error('Customer ID is required');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get company ID
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Company not found');

      const { error } = await (supabase as any)
        .from('customer_credits')
        .insert({
          customer_id: customerId,
          credit_amount: amount,
          remaining_amount: amount,
          source_type: 'manual',
          reason_type: reasonType,
          description: description || null,
          internal_description: internalDescription || null,
          created_by: user.id,
          status: 'issued',
          credit_note_issued_at: new Date().toISOString(),
          credit_note_issued_by: user.id,
        });

      if (error) throw error;

      return {};
    },
    onSuccess: () => {
      toast.success(`Manual credit created successfully`);
      queryClient.invalidateQueries({ queryKey: ['customerCredits', customerId] });
    },
    onError: (error) => {
      toast.error(`Failed to create credit: ${error.message}`);
    },
  });

  return {
    credits,
    isLoading,
    totalAvailableCredit,
    activeCredits,
    applyCredit: applyCreditMutation.mutate,
    isApplying: applyCreditMutation.isPending,
    refundCredit: refundCreditMutation.mutate,
    isRefunding: refundCreditMutation.isPending,
    cancelCredit: cancelCreditMutation.mutate,
    isCancelling: cancelCreditMutation.isPending,
    createManualCredit: createManualCreditMutation.mutate,
    isCreating: createManualCreditMutation.isPending,
  };
};

// Hook for fetching customer credits by customer ID (for invoice creation)
export const useCustomerCreditsForInvoice = (customerId: string | null) => {
  const { data: credits, isLoading } = useQuery({
    queryKey: ['customerCreditsForInvoice', customerId],
    queryFn: async () => {
      if (!customerId) return [];
      
      const { data, error } = await supabase
        .from('customer_credits')
        .select('id, credit_amount, remaining_amount, source_type, description')
        .eq('customer_id', customerId)
        .eq('status', 'issued')
        .gt('remaining_amount', 0);

      if (error) throw error;
      return data;
    },
    enabled: !!customerId,
  });

  const totalAvailableCredit = credits?.reduce((sum, c) => sum + c.remaining_amount, 0) || 0;

  return {
    credits,
    isLoading,
    totalAvailableCredit,
    hasAvailableCredit: totalAvailableCredit > 0,
  };
};
